import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

/* ═══════════════════════════════════════════════════════════════════
   MERCH + DONATE SECTION — "Two ways to support the mission"
   Combines the donate CTA with a merch shop link.
   ═══════════════════════════════════════════════════════════════════ */

// ─── STORE URL ───
const MERCH_STORE_URL = 'https://merch.whoareyoumakingrich.com';

const MERCH_PRODUCTS = [
  { name: '$WYMR? — The Original Ringer Tee', desc: 'Unisex Ringer Tee', price: '$19.57', img: '/assets/images/wymr-tee.jpg', url: `${MERCH_STORE_URL}/product/26908769` },
  { name: '$WYMR? — Unisex Tee', desc: 'Classic Unisex Tee', price: '$17.41', img: '/assets/images/wymr-unisex-tee.jpg', url: `${MERCH_STORE_URL}/product/26932090` },
  { name: '$WYMR? — Unisex Tee (Alt)', desc: 'Classic Unisex Tee · Variant', price: '$17.41', img: '/assets/images/wymr-unisex-tee-2.jpg', url: `${MERCH_STORE_URL}/product/26929329` },
  { name: '$WYMR? — Hoodie', desc: 'Cozy Pullover Hoodie', price: '$32.75', img: '/assets/images/wymr-hoodie.jpg', url: `${MERCH_STORE_URL}/product/26928864` },
  { name: '$WYMR? — Baseball Tee', desc: 'Unisex Baseball Tee', price: '$24.29', img: '/assets/images/wymr-baseball-tee.jpg', url: `${MERCH_STORE_URL}/product/26930016` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 1', price: '$66.19', img: '/assets/images/wymr-puffer.jpg', url: `${MERCH_STORE_URL}/product/26930618` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 2', price: '$66.19', img: '/assets/images/wymr-puffer-2.jpg', url: `${MERCH_STORE_URL}/product/26930658` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 3', price: '$66.19', img: '/assets/images/wymr-puffer-3.jpg', url: `${MERCH_STORE_URL}/product/26930674` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 4', price: '$66.19', img: '/assets/images/wymr-puffer-4.jpg', url: `${MERCH_STORE_URL}/product/26931013` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 5', price: '$66.19', img: '/assets/images/wymr-puffer-5.jpg', url: `${MERCH_STORE_URL}/product/26931092` },
  { name: '$WYMR? — Snapback Cap', desc: 'Snapback Trucker', price: '$23.38', img: '/assets/images/wymr-snapback.jpg', url: `${MERCH_STORE_URL}/product/26932202` },
  { name: '$WYMR? — Trucker Cap', desc: 'Trucker Cap · Side View', price: '$16.59', img: '/assets/images/wymr-trucker-1.jpg', url: `${MERCH_STORE_URL}/product/26932476` },
  { name: '$WYMR? — Trucker Cap', desc: 'Trucker Cap · Front View', price: '$16.59', img: '/assets/images/wymr-trucker-2.jpg', url: `${MERCH_STORE_URL}/product/26932682` },
  { name: '$WYMR? — Racerback Tank', desc: "Women's Racerback Tank", price: '$23.33', img: '/assets/images/wymr-racerback.jpg', url: `${MERCH_STORE_URL}/product/26929902` },
  { name: '$WYMR? — Tank Top', desc: "Women's Spaghetti Strap", price: '$33.92', img: '/assets/images/wymr-tank-top.jpg', url: `${MERCH_STORE_URL}/product/26929798` },
];

/* ─── MERCH CAROUSEL ─────────────────────────────────────────────── */
const MerchCarousel = ({ products }) => {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const mouseStart = useRef(null);
  const minSwipe = 40;

  const handleTouchStart = (e) => { touchEnd.current = null; touchStart.current = e.targetTouches[0].clientX; };
  const handleTouchMove = (e) => { touchEnd.current = e.targetTouches[0].clientX; };
  const handleTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    const d = touchStart.current - touchEnd.current;
    if (Math.abs(d) >= minSwipe) {
      if (d > 0 && current < products.length - 1) setCurrent(p => p + 1);
      else if (d < 0 && current > 0) setCurrent(p => p - 1);
    }
  };
  const handleMouseDown = (e) => { mouseStart.current = e.clientX; };
  const handleMouseUp = (e) => {
    if (mouseStart.current === null) return;
    const d = mouseStart.current - e.clientX;
    if (Math.abs(d) >= minSwipe) {
      if (d > 0 && current < products.length - 1) setCurrent(p => p + 1);
      else if (d < 0 && current > 0) setCurrent(p => p - 1);
    }
    mouseStart.current = null;
  };

  const p = products[current];

  // Track if user swiped so we don't trigger link on swipe
  const didSwipe = useRef(false);

  const wrappedTouchEnd = () => {
    if (touchStart.current && touchEnd.current) {
      const d = Math.abs(touchStart.current - touchEnd.current);
      didSwipe.current = d >= minSwipe;
    }
    handleTouchEnd();
  };

  const wrappedMouseUp = (e) => {
    if (mouseStart.current !== null) {
      const d = Math.abs(mouseStart.current - e.clientX);
      didSwipe.current = d >= 5;
    }
    handleMouseUp(e);
  };

  const handleCardClick = () => {
    if (!didSwipe.current && p.url) {
      window.open(p.url, '_blank', 'noopener,noreferrer');
    }
    didSwipe.current = false;
  };

  return (
    <div className="mb-6">
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={wrappedTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={wrappedMouseUp}
        onClick={handleCardClick}
        className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-xl border border-amber-500/10 p-4 cursor-grab active:cursor-grabbing select-none hover:border-amber-500/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg flex-shrink-0 shadow-md bg-white overflow-hidden">
            <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-opacity duration-300" draggable={false} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm truncate">{p.name}</p>
            <p className="text-gray-400 text-xs mt-0.5">{p.desc}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-amber-400 font-bold text-sm">From {p.price}</span>
              <span className="text-[9px] text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">FREE shipping*</span>
            </div>
            <p className="text-amber-400 text-[10px] font-semibold mt-1.5 hover:text-amber-300 transition-colors">
              View in Store →
            </p>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-1.5 mt-2.5">
        {products.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`rounded-full transition-all duration-200 ${
              i === current ? 'w-5 h-1.5 bg-amber-500' : 'w-1.5 h-1.5 bg-gray-600 hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-[9px] text-gray-500 mt-1">← Swipe to browse {products.length} products →</p>
    </div>
  );
};

const MerchSection = ({ onDonateClick }) => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#020a18] via-[#051225] to-[#0a1628] py-20 px-4">
      {/* Decorative background glow */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[120px]" />
      <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[100px]" />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-gray-400 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            100% goes to servers & research
          </div>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">
            Two Ways to Keep Transparency Alive
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Every donation and every purchase directly funds the servers, research engine, and data 
            that power this platform. Pick the way that works for you — both make a real difference.
          </p>
        </div>

        {/* Two cards side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* ── DONATE CARD ── */}
          <div className="group relative rounded-2xl overflow-hidden">
            {/* Glow border */}
            <div className="absolute -inset-[1px] bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 rounded-2xl group-hover:from-emerald-500/50 group-hover:to-cyan-500/50 transition-all duration-500" />
            <div className="relative bg-[#0A1628] rounded-2xl p-8 h-full flex flex-col">
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/20 group-hover:shadow-emerald-500/40 transition-shadow">
                <Icon name="Heart" size={26} className="text-white" />
              </div>

              <h3 className="font-heading font-bold text-xl text-white mb-2">
                Donate Directly
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                From as little as <strong className="text-emerald-400">$0.50</strong> — every cent goes to keeping the Supabase database running, 
                the research agent discovering connections, and the platform free for everyone.
              </p>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { label: 'Server Cost', value: '~$75/mo', icon: 'Server' },
                  { label: 'Research', value: '~$40/mo', icon: 'Cpu' },
                  { label: 'Domain/SSL', value: '~$15/yr', icon: 'Shield' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-lg p-2.5 text-center border border-white/5">
                    <Icon name={stat.icon} size={14} className="text-cyan-400 mx-auto mb-1" />
                    <div className="text-white font-bold text-sm">{stat.value}</div>
                    <div className="text-gray-500 text-[9px] uppercase tracking-wide">{stat.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={onDonateClick}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-emerald-400 hover:to-cyan-500 transition-all flex items-center justify-center gap-2.5 text-[15px]"
              >
                <Icon name="Heart" size={18} />
                Donate Now
              </button>

              <p className="text-center text-[10px] text-gray-500 mt-3 flex items-center justify-center gap-3">
                <span className="flex items-center gap-1"><Icon name="Shield" size={10} className="text-green-400" /> Encrypted</span>
                <span className="flex items-center gap-1"><Icon name="Lock" size={10} className="text-blue-400" /> Secure</span>
                <span>PayPal · Stripe · Revolut</span>
              </p>
            </div>
          </div>

          {/* ── MERCH CARD ── */}
          <div className="group relative rounded-2xl overflow-hidden">
            {/* Glow border */}
            <div className="absolute -inset-[1px] bg-gradient-to-br from-amber-500/30 to-orange-500/30 rounded-2xl group-hover:from-amber-500/50 group-hover:to-orange-500/50 transition-all duration-500" />
            <div className="relative bg-[#0A1628] rounded-2xl p-8 h-full flex flex-col">
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-5 shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/40 transition-shadow">
                <Icon name="ShoppingBag" size={26} className="text-white" />
              </div>

              <h3 className="font-heading font-bold text-xl text-white mb-2">
                Grab Some Merch
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                Rock the <strong className="text-amber-400">$WYMR?</strong> tee and turn heads. Every shirt sparks a conversation about 
                corporate transparency — and 100% of profits go right back to funding the platform.
              </p>

              {/* Swipeable product carousel */}
              <MerchCarousel products={MERCH_PRODUCTS} />

              <a
                href={MERCH_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-amber-400 hover:to-orange-500 transition-all flex items-center justify-center gap-2.5 text-[15px]"
              >
                <Icon name="ShoppingBag" size={18} />
                Browse Merch Store
              </a>

              <p className="text-center text-[10px] text-gray-500 mt-3 flex items-center justify-center gap-3">
                <span className="flex items-center gap-1"><Icon name="Truck" size={10} className="text-amber-400" /> Print-on-demand</span>
                <span className="flex items-center gap-1"><Icon name="Heart" size={10} className="text-red-400" /> 100% profits to servers</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom tagline */}
        <p className="text-center text-gray-500 text-sm mt-10 max-w-lg mx-auto leading-relaxed">
          <strong className="text-gray-400">Both help keep the servers and services running.</strong><br />
          Whether you donate $0.50 or grab a tee, you're directly funding corporate transparency for everyone.
        </p>
      </div>
    </section>
  );
};

export default MerchSection;
